// Search page — users + posts
import React, { useState, useRef, useEffect } from 'react';
import { FeedPost } from '../types';
import { MomentCard } from '../components/MomentCard';
import { EmptyState } from '../components/EmptyState';
import { useSession } from '../hooks/useSession';

type UserResult = {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
};

interface Props {
  onNavigate: (page: string, params?: Record<string, string>) => void;
  onLoginRequired: () => void;
}

export function SearchPage({ onNavigate, onLoginRequired }: Props) {
  const session = useSession();
  const currentUser = session.status === 'authenticated' ? session.user : null;

  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<UserResult[]>([]);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);


  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) { setUsers([]); setPosts([]); setSearched(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/v1/search?q=${encodeURIComponent(q)}`);
        const json = await res.json() as { users: UserResult[]; posts: FeedPost[] };
        setUsers(json.users ?? []);
        setPosts(json.posts ?? []);
        setSearched(true);
      } catch {
        setSearched(true);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  function navigateUser(username: string) {
    onNavigate('user', { username });
  }

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <h2 style={{ fontSize: 17, fontWeight: 600 }}>Search</h2>
      </header>

      <div style={searchBarWrap}>
        <SearchIcon />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search people or songs..."
          style={inputStyle}
          autoFocus
          autoComplete="off"
        />
        {query && (
          <button onClick={() => setQuery('')} style={clearBtnStyle} aria-label="Clear">
            <ClearIcon />
          </button>
        )}
      </div>

      <div style={resultsStyle}>
        {loading && <div style={mutedStyle}>Searching...</div>}

        {!loading && searched && users.length === 0 && posts.length === 0 && (
          <EmptyState heading={`No results for "${query}"`} body="Try a different track name, artist, or username." />
        )}

        {/* User results */}
        {users.length > 0 && (
          <section>
            <div style={sectionLabel}>People</div>
            {users.map(u => (
              <button
                key={u.id}
                style={userRowStyle}
                onClick={() => u.username && navigateUser(u.username)}
              >
                <UserAvatar url={u.avatar_url} name={u.display_name ?? u.username ?? '?'} />
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.display_name ?? u.username}
                  </div>
                  {u.username && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>@{u.username}</div>
                  )}
                </div>
              </button>
            ))}
          </section>
        )}

        {/* Post results */}
        {posts.length > 0 && (
          <section style={{ marginTop: users.length > 0 ? 24 : 0 }}>
            <div style={sectionLabel}>Moments</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {posts.map(post => (
                <MomentCard
                  key={post.id}
                  post={post}
                  currentUserId={currentUser?.id}
                  onLoginRequired={onLoginRequired}
                  onResonate={() => {}}
                  onReply={p => {
                    if (!currentUser) { onLoginRequired(); return; }
                    onNavigate('post', { reply_to: p.id });
                  }}
                  onOpen={p => onNavigate('post-detail', { postId: p.id })}
                  onNavigateUser={navigateUser}
                  onDelete={id => setPosts(prev => prev.filter(p => p.id !== id))}
                  onUpdate={updated => setPosts(prev => prev.map(p => p.id === updated.id ? updated : p))}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function UserAvatar({ url, name }: { url: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const initial = name[0]?.toUpperCase() ?? '?';
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name}
        style={avatarStyle}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div style={{ ...avatarStyle, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600 }}>
      {initial}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--text-muted)' }}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const pageStyle: React.CSSProperties = { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' };

const headerStyle: React.CSSProperties = {
  padding: '16px 16px 8px',
  borderBottom: '1px solid var(--border)',
};

const searchBarWrap: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '12px 16px', borderBottom: '1px solid var(--border)',
  position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10,
};

const inputStyle: React.CSSProperties = {
  flex: 1, background: 'none', border: 'none', outline: 'none',
  fontSize: 15, color: 'var(--text)',
};

const clearBtnStyle: React.CSSProperties = {
  color: 'var(--text-muted)', padding: 2, flexShrink: 0,
};

const resultsStyle: React.CSSProperties = { flex: 1, padding: '16px 16px 32px' };

const sectionLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10,
};

const mutedStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 14, padding: '8px 0' };

const userRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  width: '100%', padding: '10px 0',
  borderBottom: '1px solid var(--border)', background: 'none',
  cursor: 'pointer',
};

const avatarStyle: React.CSSProperties = {
  width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
};
